import { Alert, List, Stack, Text } from '@mantine/core';

import { InboxItem } from '@/types/inbox';

interface NpMissingBackofficeBannerProps {
  /**
   * The open `np_missing_in_backoffice` inbox item for this RR. When
   * `null`, the banner renders nothing — the caller (the stepper) just
   * mounts the component unconditionally and lets it self-hide.
   */
  item: InboxItem | null;
}

/**
 * Phase 06 — banner shown at the NP_SELECTED step when the RR was
 * reverted by the worker after a Backoffice 400. Reads the inbox
 * item's `extra` payload (carrying `missing_partners`,
 * `missing_subjects`, `missing_components`) and surfaces the missing
 * emails so the analyst knows what to fix.
 *
 * Self-hides on `null`. The banner clears once the analyst re-Starts
 * successfully (auto-resolves on the backend; a refetch from the
 * parent hook then yields `null`).
 */
export function NpMissingBackofficeBanner({ item }: Readonly<NpMissingBackofficeBannerProps>) {
  if (!item) {
    return null;
  }

  const extra = (item.extra ?? {}) as {
    missing_partners?: string[];
    missing_subjects?: string[];
    missing_components?: string[];
  };
  const partners = extra.missing_partners ?? [];
  const subjects = extra.missing_subjects ?? [];
  const components = extra.missing_components ?? [];

  return (
    <Alert color="red" title={item.title} variant="light">
      <Stack gap="xs">
        {item.body && <Text size="sm">{item.body}</Text>}

        {partners.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Missing partners
            </Text>
            <List size="sm">
              {partners.map((email) => (
                <List.Item key={email}>{email}</List.Item>
              ))}
            </List>
          </Stack>
        )}

        {subjects.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Missing subjects
            </Text>
            <List size="sm">
              {subjects.map((id) => (
                <List.Item key={id}>{id}</List.Item>
              ))}
            </List>
          </Stack>
        )}

        {components.length > 0 && (
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Missing scope components
            </Text>
            <List size="sm">
              {components.map((slug) => (
                <List.Item key={slug}>{slug}</List.Item>
              ))}
            </List>
          </Stack>
        )}
      </Stack>
    </Alert>
  );
}
